import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useSidebar } from '../../contexts/SidebarContext';
import type { NavItem } from './sidebarConfig';

interface SidebarNavItemProps {
  item: NavItem;
  depth?: number;
}

export function SidebarNavItem({ item, depth = 0 }: SidebarNavItemProps) {
  const { isCollapsed, isExpanded, toggleExpanded, setMobileOpen } = useSidebar();
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const expanded = isExpanded(item.id);

  // Check if any child is active
  const isChildActive = hasChildren
    ? item.children!.some((child) =>
        child.path ? location.pathname.startsWith(child.path) : false
      )
    : false;

  // Check if this item is active
  const isActive = item.path
    ? location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    : isChildActive;

  const handleClick = () => {
    if (hasChildren) {
      toggleExpanded(item.id);
    } else {
      // Close mobile sidebar on navigation
      setMobileOpen(false);
    }
  };

  const Icon = item.icon;
  const paddingLeft = isCollapsed ? 'pl-0 justify-center' : depth === 0 ? 'pl-3' : 'pl-9';

  // Base styles
  const baseStyles = `
    flex items-center gap-3 py-2.5 rounded-lg transition-all duration-200
    ${paddingLeft}
    ${isCollapsed ? 'px-0' : 'pr-3'}
  `;

  // Active/hover styles
  const stateStyles = isActive
    ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
    : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200 border border-transparent';

  // If has children, render as button
  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={`w-full ${baseStyles} ${stateStyles}`}
          title={isCollapsed ? item.label : undefined}
        >
          <Icon className="h-5 w-5 flex-shrink-0" />
          {!isCollapsed && (
            <>
              <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
              {item.badge && (
                <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400">
                  {item.badge}
                </span>
              )}
              <ChevronRight
                className={`h-4 w-4 transition-transform duration-200 ${
                  expanded ? 'rotate-90' : ''
                }`}
              />
            </>
          )}
        </button>

        {/* Children - only show when expanded and not collapsed */}
        {!isCollapsed && expanded && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <SidebarNavItem key={child.id} item={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // If has path, render as NavLink
  if (item.path) {
    return (
      <NavLink
        to={item.path}
        onClick={handleClick}
        className={`${baseStyles} ${stateStyles}`}
        title={isCollapsed ? item.label : undefined}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        {!isCollapsed && (
          <>
            <span className="flex-1 text-sm font-medium">{item.label}</span>
            {item.badge && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400">
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  }

  return null;
}
